import { useEffect, useState, type ReactNode } from 'react';
import { apiRequest, ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { Button, DataTable } from '../../../../shared/components/index.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';

interface InstallRow {
  readonly lcncTenantInstallId: string;
  readonly installCode: string;
  readonly appCode: string;
  readonly appId: string;
  readonly versionLabel: string | null;
  readonly installStatus: string;
  readonly namespaceRef: string | null;
  readonly declaredPermissionsJson: string[] | null;
  readonly grantedPermissionsJson: string[] | null;
  readonly installedAt: string | null;
}

const STATUSES = ['INSTALLED', 'SUSPENDED', 'UNINSTALLED'];

/**
 * M40 — Tenant App Installs (GR_M36-M40 §L L40.05 / §Q13). The tenant admin's
 * ledger of every marketplace app installed into THIS tenant (T534A): it shows the
 * granted permission set (least-privilege — the intersection of declared ∩
 * approved), the isolation namespace, and lets the admin suspend, resume or
 * uninstall an app. Uninstalling revokes the granted set immediately (the ledger
 * row flips to UNINSTALLED and the granted permissions are emptied). Every row is
 * tenant-scoped so a cross-tenant install can never appear here (LAW 4). Viewing
 * needs TENANT_INSTALL_VIEW; lifecycle actions need TENANT_INSTALL_MANAGE.
 */
export function TenantInstallsConsole(): ReactNode {
  const { token } = useAuth();
  const { t } = useTranslation();
  // Instants follow the tenant's DISPLAY_DATE_FORMAT, not the reader's language picker.
  const { at } = useBusinessDay();
  const [rows, setRows] = useState<InstallRow[]>([]);
  const [installStatus, setInstallStatus] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);
  const [notice, setNotice] = useState<string | null>(null);
  const [effective, setEffective] = useState<{ id: string; perms: string[] } | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams();
    if (installStatus) params.set('installStatus', installStatus);
    apiRequest<{ data: InstallRow[] }>(`/v1/app-platform/installs?${params.toString()}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, installStatus, reload]);

  const act = async (r: InstallRow, action: 'suspend' | 'resume'): Promise<void> => {
    setNotice(null);
    try {
      await apiRequest(`/v1/app-platform/installs/${r.lcncTenantInstallId}/${action}`, { method: 'POST', body: {}, token });
      setNotice(t('m40.tenantInstalls.actNotice', { code: r.installCode, action: t(`m40.tenantInstalls.actWord.${action}`) }));
      setReload((n) => n + 1);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setError(t('m40.tenantInstalls.notAuth'));
      } else {
        setError((err as Error).message);
      }
    }
  };

  const uninstall = async (r: InstallRow): Promise<void> => {
    setNotice(null);
    try {
      await apiRequest(`/v1/app-platform/installs/${r.lcncTenantInstallId}/uninstall`, {
        method: 'POST',
        body: { reason: 'Uninstalled by tenant admin' },
        token,
      });
      setNotice(t('m40.tenantInstalls.uninstalled', { code: r.installCode }));
      setReload((n) => n + 1);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setError(t('m40.tenantInstalls.notAuth'));
      } else {
        setError((err as Error).message);
      }
    }
  };

  const showEffective = async (r: InstallRow): Promise<void> => {
    try {
      const res = await apiRequest<{ data: string[] }>(`/v1/app-platform/installs/${r.lcncTenantInstallId}/effective-permissions`, { token });
      setEffective({ id: r.lcncTenantInstallId, perms: res.data });
    } catch (err) {
      setError((err as Error).message);
    }
  };

  return (
    <main aria-label="tenant-installs">
      <h1>{t('m40.tenantInstalls.title')}</h1>

      <section aria-label="installs-filters">
        <label>{t('m40.common.status')}
          <select aria-label="filter-status" value={installStatus} onChange={(e) => setInstallStatus(e.target.value)}>
            <option value="">{t('m40.tenantInstalls.allStatuses')}</option>
            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </label>
      </section>

      {notice && <p aria-label="install-notice">{notice}</p>}
      {error && <p role="alert">{error}</p>}

      {loading
        ? <p>{t('m40.common.loading')}</p>
        : (
          <DataTable<InstallRow>
            ariaLabel="installs-table"
            testId="installs-table"
            rowKey={(r) => r.lcncTenantInstallId}
            rowAriaLabel={(r) => `install-${r.installCode}`}
            emptyMessage={t('m40.tenantInstalls.empty')}
            rows={rows}
            columns={[
              { key: 'install', header: t('m40.tenantInstalls.colInstall'), render: (r) => r.installCode },
              { key: 'app', header: t('m40.tenantInstalls.colApp'), render: (r) => r.appCode },
              { key: 'version', header: t('m40.common.version'), render: (r) => r.versionLabel ?? '—' },
              { key: 'status', header: t('m40.common.status'), render: (r) => r.installStatus },
              { key: 'namespace', header: t('m40.tenantInstalls.colNamespace'), render: (r) => <code>{r.namespaceRef ?? '—'}</code> },
              { key: 'granted', header: t('m40.tenantInstalls.colGranted'), render: (r) => (r.grantedPermissionsJson ?? []).length },
              { key: 'installed', header: t('m40.tenantInstalls.colInstalled'), render: (r) => r.installedAt ? at(r.installedAt) : '—' },
              { key: 'actions', header: t('m40.common.actions'), render: (r) => (
                <>
                  <Button variant="secondary" type="button" aria-label={`perms-${r.installCode}`} onClick={() => showEffective(r)}>{t('m40.tenantInstalls.permissions')}</Button>
                  {r.installStatus === 'INSTALLED' && (
                    <Button variant="secondary" type="button" aria-label={`suspend-${r.installCode}`} onClick={() => act(r, 'suspend')}>{t('m40.tenantInstalls.suspend')}</Button>
                  )}
                  {r.installStatus === 'SUSPENDED' && (
                    <Button variant="secondary" type="button" aria-label={`resume-${r.installCode}`} onClick={() => act(r, 'resume')}>{t('m40.tenantInstalls.resume')}</Button>
                  )}
                  {r.installStatus !== 'UNINSTALLED' && (
                    <Button variant="danger" type="button" aria-label={`uninstall-${r.installCode}`} onClick={() => uninstall(r)}>{t('m40.tenantInstalls.uninstall')}</Button>
                  )}
                </>
              ) },
            ]}
          />
        )}

      {effective && (
        <section aria-label="effective-permissions">
          <h2>{t('m40.tenantInstalls.effectiveHeading')}</h2>
          {effective.perms.length === 0
            ? <p aria-label="no-effective-perms">{t('m40.tenantInstalls.noEffective')}</p>
            : (
              <ul aria-label="effective-perms-list">
                {effective.perms.map((p) => <li key={p}>{p}</li>)}
              </ul>
            )}
          <Button variant="secondary" type="button" onClick={() => setEffective(null)}>{t('m40.tenantInstalls.close')}</Button>
        </section>
      )}
    </main>
  );
}
